import { HiCheckCircle, HiExclamationCircle, HiXCircle, HiInformationCircle } from "react-icons/hi";
import { LuCircleDot } from "react-icons/lu";
import Loader from "../../components/form-loader/form-loader";

/**
 * Presentational pieces shared by the subscription tabs (Usage, Plans, Invoices, Payment methods).
 * Same card / pill language as the agent pages: gray-200 borders, rounded-xl cards, black primary, lime accent.
 */

export const SUBSCRIPTION_BASE = "/user-settings/subscription";

export function SectionCard({ title, description, action, children, className = "", bodyClassName = "p-5" }) {
	return (
		<section className={`rounded-xl border border-gray-200 bg-white ${className}`}>
			{(title || action) && (
				<div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
					<div className="min-w-0">
						{title && <h2 className="text-sm font-semibold text-gray-900">{title}</h2>}
						{description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
					</div>
					{action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
				</div>
			)}
			<div className={bodyClassName}>{children}</div>
		</section>
	);
}

const PILL_TONES = {
	success: { icon: HiCheckCircle, className: "text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/20" },
	warning: { icon: HiExclamationCircle, className: "text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20" },
	danger: { icon: HiXCircle, className: "text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/20" },
	info: { icon: HiInformationCircle, className: "text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20" },
	neutral: { icon: LuCircleDot, className: "text-gray-600 bg-gray-100" },
	accent: { icon: null, className: "text-gray-900 bg-brand-lime/40 dark:bg-brand-lime/20 dark:text-brand-lime" },
};

export function Pill({ tone = "neutral", children, icon = true, className = "" }) {
	const style = PILL_TONES[tone] || PILL_TONES.neutral;
	const Icon = icon ? style.icon : null;
	return (
		<span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${style.className} ${className}`}>
			{Icon && <Icon className="h-3.5 w-3.5" />}
			{children}
		</span>
	);
}

// Inline banner for page level information and errors, replaces stacked flowbite alerts
export function Notice({ tone = "info", title, children, action }) {
	const styles = {
		info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200",
		warning: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200",
		danger: "border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200",
		success: "border-green-200 bg-green-50 text-green-900 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-200",
	};
	const icons = { info: HiInformationCircle, warning: HiExclamationCircle, danger: HiXCircle, success: HiCheckCircle };
	const Icon = icons[tone] || HiInformationCircle;
	return (
		<div className={`flex flex-wrap items-start gap-3 rounded-xl border px-4 py-3 text-sm ${styles[tone] || styles.info}`}>
			<Icon className="mt-0.5 h-5 w-5 shrink-0" />
			<div className="min-w-0 flex-1">
				{title && <p className="font-semibold">{title}</p>}
				{children && <div className={title ? "mt-0.5" : ""}>{children}</div>}
			</div>
			{action && <div className="shrink-0">{action}</div>}
		</div>
	);
}

export function LoadingState({ text = "Loading..." }) {
	return (
		<div className="flex items-center justify-center rounded-xl border border-dashed border-gray-200 py-16">
			<Loader text={text} />
		</div>
	);
}

export function EmptyState({ icon: Icon, title, description, action }) {
	return (
		<div className="flex flex-col items-center justify-center px-6 py-12 text-center">
			{Icon && (
				<div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500">
					<Icon className="h-5 w-5" />
				</div>
			)}
			<p className="text-sm font-semibold text-gray-900">{title}</p>
			{description && <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>}
			{action && <div className="mt-4">{action}</div>}
		</div>
	);
}

// Usage bar: black fill (white in dark mode), red once the allowance is used up
export function UsageBar({ ratio }) {
	if (ratio == null) {
		return null;
	}
	const percent = Math.min(Math.round(ratio * 100), 100);
	const tone = ratio >= 1 ? "bg-red-500" : ratio >= 0.8 ? "bg-amber-500" : "bg-primary";
	return (
		<div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
			<div className={`h-full rounded-full transition-all ${tone}`} style={{ width: `${percent}%` }} />
		</div>
	);
}
